import { FC, lazy } from 'react'
import { Route, Switch } from 'react-router-dom'
import { AgentDynamicScheduling } from './App/AgentDynamicScheduling'
import { AgentExplorer } from './App/AgentExplorer'
import { Analytics } from './App/Analytics'
import { ApplicationManagement } from './App/ApplicationManagement'
import { BulkContactExportMonitoring } from './App/ApplicationManagement/BulkContactExportMonitoring'
import { ContactQueuePage } from './App/ContactQueue'
import { CreateNewGoal, ViewGoal } from './App/ApplicationManagement/ContactGoalAdmin/ContactGoalForm'
import { ContactGoalAdminPage } from './App/ApplicationManagement/ContactGoalAdmin'
import { Messaging } from './App/Messaging'
import { RecordingControls } from './App/RecordingControls'
import { Reports } from './App/Reports'
import { Schedule } from './App/Schedule'
import { TranscriptionSearch } from './App/TranscriptionSearch'
import Status404NotFound from './Errors/Status404NotFound'
import * as PAGE_PERMISSIONS from './Permissions/pagePermissions.constants'
import { PrivateRoute } from './Permissions/PrivateRoute'
import RootRoute from './Permissions/RootRoute'
import { FEATURE_FLAGS } from './FeatureFlags/featureFlags.constants'
import { BotAnalytics } from './App/BotAnalytics'
import { TrendingTopics } from './App/Analytics/TrendingTopics'
import { AutoQA } from './App/Analytics/AutoQA'
import { AutoQAFormManager } from './App/ApplicationManagement/Analytics/AutoQAFormManager'
import { SmartTags } from './App/Analytics/SmartTags'
import { SmartTagsApp } from './App/MicroApps/SmartTagsApp'
import { WelcomePage } from './Common/WelcomePage'

export const ROUTE_PATHS = {
  AGENT_DYNAMIC_SCHEDULING: '/agentDynamicScheduling',
  AGENT_EXPLORER: '/agentExplorer',
  ANALYTICS_MAIL_SERVER: '/admin/analyticsMailServer',
  ANALYTICS_SUBSTITUTION: '/admin/analyticsSubstitution',
  ANALYTICS: '/analytics',
  APIDOCUMENTATION: '/apiDocumentation',
  APPLICATION_MANAGEMENT: '/admin',
  BULK_CONTACT_EXPORT_MONITORING: '/admin/bulkContactExportMonitoring',
  CONTACT_QUEUE: '/contactQueue',
  CONTACT_GOAL_CREATE: '/admin/contactGoal/create',
  CONTACT_GOAL_VIEW: '/admin/contactGoal/:goalId/:mode',
  CONTACT_GOAL_ADMIN: '/admin/contactGoal',
  DASHBOARD: '/dashboard',
  DATA_EXPLORER: '/dataExplorer',
  AR_ASSET_BROWSER: '/dataExplorer/reports',
  AR_DATA_LIBRARY: '/dataExplorer/dataLibrary',
  CDM_SETTINGS: '/dataExplorer/settings',
  BI_INSIGHTS: '/insights/:assetViewType?/:assetId?',
  CDM_SHARING_GROUP_ADMIN: '/admin/sharingGroups',
  DEVICE_ASSOCIATIONS: '/admin/deviceAssociations',
  EVALUATION_FORM_GROUPS: '/admin/evaluationFormGroups',
  EVALUATION_FORM_MANAGER: '/admin/evaluationFormManager',
  BULK_CONTACT_OPERATIONS: '/admin/bulkContactOperations',
  INCLUSION_EXCLUSION: '/admin/inclusionExclusion',
  LANDINGPAGE_MANAGEMENT: '/admin/landingPageManagement',
  MESSAGING: '/messaging',
  OUTGOING_MAIL_SERVER: '/admin/outgoingMailServer',
  PERFORMANCE_COACHING: '/performanceCoaching',
  PERFORMANCE_COACHING_AGENT_DASHBOARD: '/performanceCoaching/AgentPerformance',
  PERFORMANCE_COACHING_COACH_DASHBOARD: '/performanceCoaching/TeamPerformance',
  PERFORMANCE_COACHING_ORG_DASHBOARD: '/performanceCoaching/OrganizationPerformance',
  PERFORMANCE_COACHING_ADMIN_DASHBOARD: '/performanceCoaching/MetricAdministration',
  PERFORMANCE_COACHING_SETTINGS: '/admin/performanceCoachingSettings',
  QM_GLOBAL_SETTINGS: '/admin/qmGlobalSettings',
  DATA_TASK_MANAGEMENT: '/admin/dataTaskManagement',
  TIME_OFF_REPORT: '/admin/timeOffReport',
  USER_ATTRIBUTES_MANAGEMENT: '/admin/userAttributesManagement',
  UDM_CONNECTOR_MANAGEMENT: '/admin/udm',
  RT_CONNECTOR_MANAGEMENT: '/admin/rt',
  RECORDING_CONTROLS: '/recordingControls',
  RECORDINGS: '/recordings',
  REPORTS: '/reports',
  SCHEDULE: '/scheduling',
  TELEOPTI_WFM: '/wfm',
  TEAMS: '/admin/teams',
  TWILIO_CONFIGURATION: '/admin/twilioConfig',
  NEW_TASK_MANAGER: '/admin/taskManager',
  TASK_MANAGER_CREATE: '/admin/taskManager/(create|edit)',
  PHRASE_MANAGER: '/admin/phraseManager',
  ANALYTICS_CONFIG: '/admin/analyticsConfig',
  TRANSCRIPTION_SEARCH: '/analytics/transcriptionSearch',
  UJET_CONFIGURATION: '/admin/uJetConfig',
  GENESYS_CONFIGURATION: '/admin/genesysConfig',
  IAM_INTEGRATION: '/admin/iamIntegration',
  CISCO_CONFIGURATION: '/admin/ciscoConfig',
  CISCO_WEBEX_CONFIGURATION: '/admin/webexConfig',
  LEGACY_API_ACCESS: '/admin/legacyApiAccess',
  CUSTOMER_IDP_CONFIG: '/admin/customerIdpConfig',
  RING_CENTRAL_CONFIGURATION: '/admin/ringCentralConfig',
  FIVE9_ARU_CONFIGURATION: '/admin/five9AruConfig/:acdId?',
  AVAYA_AXP_PUBLIC_CONFIGURATION: '/admin/avayaAxpPublicConfig',
  TRANSFER_USER_DATA: '/admin/transferUserData',
  BOT_ANALYTICS: '/botAnalytics',
  TRENDING_TOPICS: '/trendingTopics',
  AUTO_QA: '/autoQA',
  AUTO_QA_FORM_MANAGER: '/AutoQAFormManager',
  SMART_TAGS: '/smartTags',
  SMART_TAG_APP: '/smartTag/*',
  WELCOME_PAGE: '/welcomePage',
}

/**
 * Routes order matters when it comes to permissions and feature flags
 * Keep more specific nested-routes above parent routes
 *
 * i.e. If the user tries to navigate to /dataExplorer/dataLibrary
 * and the private route for /dataExplorer is specified before the one for /dataExplorer/dataLibrary
 * then the permissions logic will check /dataExplorer and continue navigating to dataLibrary page
 */

// Keeps from re-loading the entire DX app upon navigating within DX
const dataExplorer = lazy(() => import('./App/DataExplorer'))

export const Routes: FC = () => (
  <Switch>
    <Route exact path="/" component={RootRoute} />
    <PrivateRoute
      path={ROUTE_PATHS.INCLUSION_EXCLUSION}
      component={lazy(() => import('./App/ApplicationManagement/InclusionExclusion'))}
      permissions={PAGE_PERMISSIONS.INCLUSION_EXCLUSION}
    />
    <PrivateRoute
      path={ROUTE_PATHS.ANALYTICS_SUBSTITUTION}
      component={lazy(() => import('./App/ApplicationManagement/Analytics/Substitution'))}
      permissions={PAGE_PERMISSIONS.ANALYTICS_SUBSTITUTION}
    />
    <PrivateRoute
      path={ROUTE_PATHS.TASK_MANAGER_CREATE}
      component={lazy(() => import('./App/ApplicationManagement/Analytics/TaskManager/Form'))}
      permissions={PAGE_PERMISSIONS.TASK_MANAGER}
    />
    <PrivateRoute
      path={ROUTE_PATHS.NEW_TASK_MANAGER}
      component={lazy(() => import('./App/ApplicationManagement/Analytics/TaskManager'))}
      permissions={PAGE_PERMISSIONS.TASK_MANAGER}
    />
    <PrivateRoute
      path={ROUTE_PATHS.ANALYTICS_CONFIG}
      component={lazy(() => import('./App/ApplicationManagement/Analytics/AnalyticsConfig'))}
      permissions={PAGE_PERMISSIONS.ANALYTICS_CONFIGURATION}
    />
    <PrivateRoute
      path={ROUTE_PATHS.DEVICE_ASSOCIATIONS}
      component={lazy(() => import('./App/ApplicationManagement/DeviceAssociations'))}
      permissions={PAGE_PERMISSIONS.DEVICE_ASSOCIATIONS}
    />
    <PrivateRoute
      path={ROUTE_PATHS.PHRASE_MANAGER}
      component={lazy(() => import('./App/ApplicationManagement/Analytics/PhraseManager'))}
      permissions={PAGE_PERMISSIONS.PHRASE_MANAGER}
    />
    <PrivateRoute
      path={ROUTE_PATHS.EVALUATION_FORM_GROUPS}
      component={lazy(() => import('./App/ApplicationManagement/EvaluationFormGroups'))}
      permissions={PAGE_PERMISSIONS.EVALUATION_FORM_GROUPS}
      featureFlag="qm.tenant.enableMultipleEvalForms"
    />
    <PrivateRoute
      path={ROUTE_PATHS.EVALUATION_FORM_MANAGER}
      component={lazy(() => import('./App/ApplicationManagement/EvaluationFormManager'))}
      permissions={PAGE_PERMISSIONS.EVALUATION_FORM_MANAGER}
    />
    <PrivateRoute
      path={ROUTE_PATHS.BULK_CONTACT_OPERATIONS}
      component={lazy(() => import('./App/ApplicationManagement/BulkContactOperations'))}
      permissions={PAGE_PERMISSIONS.BULK_CONTACT_OPERATIONS}
    />
    <PrivateRoute
      path={ROUTE_PATHS.OUTGOING_MAIL_SERVER}
      component={lazy(() => import('./App/ApplicationManagement/OutgoingMailServer'))}
      permissions={PAGE_PERMISSIONS.OUTGOING_MAIL_SERVER}
    />
    <PrivateRoute
      path={ROUTE_PATHS.ANALYTICS_MAIL_SERVER}
      component={lazy(() => import('./App/ApplicationManagement/AnalyticsMailServer'))}
      permissions={PAGE_PERMISSIONS.ANALYTICS_MAIL_SERVER}
    />
    <PrivateRoute
      path={ROUTE_PATHS.QM_GLOBAL_SETTINGS}
      component={lazy(() => import('./App/ApplicationManagement/QmGlobalSettings'))}
      permissions={PAGE_PERMISSIONS.QM_GLOBAL_SETTINGS}
    />
    <PrivateRoute
      path={ROUTE_PATHS.GENESYS_CONFIGURATION}
      component={lazy(() => import('./App/ApplicationManagement/GenesysConfiguration'))}
      permissions={PAGE_PERMISSIONS.CCAAS_CONFIGURATION}
      featureFlag={FEATURE_FLAGS.GENESYS_CONFIG}
    />
    <PrivateRoute
      path={ROUTE_PATHS.TEAMS}
      component={lazy(() => import('./App/ApplicationManagement/Teams'))}
      permissions={PAGE_PERMISSIONS.TEAMS}
    />
    <PrivateRoute
      path={ROUTE_PATHS.TWILIO_CONFIGURATION}
      component={lazy(() => import('./App/ApplicationManagement/TwilioConfiguration'))}
      permissions={PAGE_PERMISSIONS.CCAAS_CONFIGURATION}
    />
    <PrivateRoute
      path={ROUTE_PATHS.UJET_CONFIGURATION}
      component={lazy(() => import('./App/ApplicationManagement/UJetConfiguration'))}
      permissions={PAGE_PERMISSIONS.CCAAS_CONFIGURATION}
    />
    <PrivateRoute
      path={ROUTE_PATHS.CISCO_CONFIGURATION}
      component={lazy(() => import('./App/ApplicationManagement/CiscoConfiguration'))}
      permissions={PAGE_PERMISSIONS.CCAAS_CONFIGURATION}
    />
    <PrivateRoute
      path={ROUTE_PATHS.CISCO_WEBEX_CONFIGURATION}
      component={lazy(() => import('./App/ApplicationManagement/CiscoWebexConfiguration'))}
      permissions={PAGE_PERMISSIONS.CCAAS_CONFIGURATION}
    />
    <PrivateRoute
      path={ROUTE_PATHS.RING_CENTRAL_CONFIGURATION}
      component={lazy(() => import('./App/ApplicationManagement/RingCentralConfiguration'))}
      permissions={PAGE_PERMISSIONS.CCAAS_CONFIGURATION}
    />
    <PrivateRoute
      path={ROUTE_PATHS.FIVE9_ARU_CONFIGURATION}
      component={lazy(() => import('./App/ApplicationManagement/Five9AruConfiguration'))}
      permissions={PAGE_PERMISSIONS.CCAAS_CONFIGURATION}
    />
    <PrivateRoute
      path={ROUTE_PATHS.AVAYA_AXP_PUBLIC_CONFIGURATION}
      component={lazy(() => import('./App/ApplicationManagement/AvayaAxpPublicConfiguration'))}
      permissions={PAGE_PERMISSIONS.CCAAS_CONFIGURATION}
    />
    <PrivateRoute
      path={ROUTE_PATHS.LEGACY_API_ACCESS}
      component={lazy(() => import('./App/ApplicationManagement/LegacyAPIAccess'))}
      permissions={PAGE_PERMISSIONS.LEGACY_API_ACCESS}
    />
    <PrivateRoute
      path={ROUTE_PATHS.IAM_INTEGRATION}
      component={lazy(() => import('./App/ApplicationManagement/IAMIntegration'))}
      permissions={PAGE_PERMISSIONS.IAM_INTEGRATION}
      featureFlag={FEATURE_FLAGS.IAM_INTEGRATION}
    />
    <PrivateRoute
      path={ROUTE_PATHS.PERFORMANCE_COACHING_SETTINGS}
      component={lazy(() => import('./App/ApplicationManagement/PerformanceCoachingSettings'))}
      permissions={PAGE_PERMISSIONS.APPLICATION_MANAGEMENT}
    />
    <PrivateRoute
      path={ROUTE_PATHS.TIME_OFF_REPORT}
      component={lazy(() => import('./App/ApplicationManagement/TimeOffReport'))}
      permissions={PAGE_PERMISSIONS.TIME_OFF_REPORT}
    />
    <PrivateRoute
      path={ROUTE_PATHS.DATA_TASK_MANAGEMENT}
      component={lazy(() => import('./App/ApplicationManagement/DataManagement/DataTaskManagement'))}
      permissions={PAGE_PERMISSIONS.CDM_DATA_TASK_MANAGEMENT}
      featureFlag={FEATURE_FLAGS.CDM_DATA_TASK_MANAGEMENT}
    />
    <PrivateRoute
      path={ROUTE_PATHS.USER_ATTRIBUTES_MANAGEMENT}
      component={lazy(() => import('./App/ApplicationManagement/DataManagement/UserAttributesManagement'))}
      permissions={PAGE_PERMISSIONS.APPLICATION_MANAGEMENT}
      featureFlag={FEATURE_FLAGS.CDM_USER_ADMIN}
    />
    <PrivateRoute
      path={ROUTE_PATHS.UDM_CONNECTOR_MANAGEMENT}
      component={lazy(() =>
        import('./App/ApplicationManagement/DataManagement/ConnectorManagement/UnifiedDataMartConnectorManagement'),
      )}
      permissions={PAGE_PERMISSIONS.DATA_UDM_CONNECTOR_MANAGEMENT}
      featureFlag={FEATURE_FLAGS.CDM_DATA_CONNECTOR_MANAGEMENT}
    />
    <PrivateRoute
      path={ROUTE_PATHS.RT_CONNECTOR_MANAGEMENT}
      component={lazy(() =>
        import('./App/ApplicationManagement/DataManagement/ConnectorManagement/RealTimeConnectorManagement'),
      )}
      permissions={PAGE_PERMISSIONS.DATA_RT_CONNECTOR_MANAGEMENT}
      featureFlag={FEATURE_FLAGS.CDM_DATA_CONNECTOR_MANAGEMENT}
    />
    <PrivateRoute
      path={ROUTE_PATHS.BULK_CONTACT_EXPORT_MONITORING}
      component={BulkContactExportMonitoring}
      permissions={PAGE_PERMISSIONS.BULK_CONTACT_EXPORT_MONITORING}
    />
    <PrivateRoute
      path={ROUTE_PATHS.AGENT_DYNAMIC_SCHEDULING}
      component={AgentDynamicScheduling}
      permissions={PAGE_PERMISSIONS.AGENT_DYNAMIC_SCHEDULING}
    />
    <PrivateRoute
      path={ROUTE_PATHS.AGENT_EXPLORER}
      component={AgentExplorer}
      permissions={PAGE_PERMISSIONS.AGENT_EXPLORER}
    />
    <PrivateRoute
      path={ROUTE_PATHS.ANALYTICS}
      component={Analytics}
      permissions={PAGE_PERMISSIONS.ANALYTICS}
    />
    <PrivateRoute
      path={ROUTE_PATHS.TRANSCRIPTION_SEARCH}
      component={TranscriptionSearch}
      permissions={PAGE_PERMISSIONS.TRANSCRIPTION_SEARCH}
    />
    <PrivateRoute
      path={ROUTE_PATHS.CUSTOMER_IDP_CONFIG}
      component={lazy(() => import('./App/ApplicationManagement/CustomerIDPConfig'))}
      permissions={PAGE_PERMISSIONS.CUSTOMER_IDP_CONFIG}
    />
    <PrivateRoute
      path={ROUTE_PATHS.CONTACT_QUEUE}
      component={ContactQueuePage}
      permissions={PAGE_PERMISSIONS.CONTACT_QUEUE}
    />
    <PrivateRoute
      path={ROUTE_PATHS.CONTACT_GOAL_CREATE}
      component={CreateNewGoal}
      permissions={['AdministerQMContactGoals']}
    />
    <PrivateRoute
      path={ROUTE_PATHS.CONTACT_GOAL_VIEW}
      component={ViewGoal}
      permissions={['AdministerQMContactGoals']}
    />
    <PrivateRoute
      path={ROUTE_PATHS.CONTACT_GOAL_ADMIN}
      component={ContactGoalAdminPage}
      permissions={['AdministerQMContactGoals']}
    />
    <PrivateRoute
      path={ROUTE_PATHS.DASHBOARD}
      component={lazy(() => import('./App/Dashboard'))}
      permissions={PAGE_PERMISSIONS.DASHBOARD}
    />
    <PrivateRoute
      path={ROUTE_PATHS.AR_ASSET_BROWSER}
      component={dataExplorer}
      permissions={PAGE_PERMISSIONS.ASSET_BROWSER}
      featureFlag={FEATURE_FLAGS.CDM_DATA_LIBRARY}
    />
    <PrivateRoute
      path={ROUTE_PATHS.CDM_SETTINGS}
      component={dataExplorer}
      permissions={PAGE_PERMISSIONS.CDM_SETTINGS}
      featureFlag={FEATURE_FLAGS.CDM_APP_SETTINGS}
    />
    <PrivateRoute
      path={ROUTE_PATHS.CDM_SHARING_GROUP_ADMIN}
      component={lazy(() => import('./App/ApplicationManagement/DataManagement/SharingGroupsAdministration'))}
      permissions={PAGE_PERMISSIONS.MANAGE_CDM_SHARING_GROUP}
      featureFlag={FEATURE_FLAGS.CDM_SHARING_GROUP_ADMIN}
    />
    <PrivateRoute
      path={ROUTE_PATHS.AR_DATA_LIBRARY}
      component={dataExplorer}
      permissions={PAGE_PERMISSIONS.VIEW_CDM_DATA_LIBRARY}
      featureFlag={FEATURE_FLAGS.CDM_DATA_LIBRARY}
    />
    <PrivateRoute
      path={ROUTE_PATHS.DATA_EXPLORER}
      component={dataExplorer}
      permissions={PAGE_PERMISSIONS.ASSET_BROWSER}
    />
    <PrivateRoute
      path={ROUTE_PATHS.MESSAGING}
      component={Messaging}
      permissions={PAGE_PERMISSIONS.MESSAGING}
    />
    <PrivateRoute
      path={ROUTE_PATHS.RECORDING_CONTROLS}
      component={RecordingControls}
      permissions={PAGE_PERMISSIONS.RECORDING_CONTROLS}
    />
    <PrivateRoute
      path={ROUTE_PATHS.RECORDINGS}
      component={lazy(() => import('./App/Recordings'))}
      permissions={PAGE_PERMISSIONS.RECORDINGS}
    />
    <PrivateRoute
      path={ROUTE_PATHS.APIDOCUMENTATION}
      component={lazy(() => import('./App/Swagger'))}
      permissions={PAGE_PERMISSIONS.APIDOCUMENTATION}
    />
    <PrivateRoute
      path={ROUTE_PATHS.REPORTS}
      component={Reports}
      permissions={PAGE_PERMISSIONS.REPORTS}
    />
    <PrivateRoute
      path={ROUTE_PATHS.SCHEDULE}
      component={Schedule}
      permissions={PAGE_PERMISSIONS.SCHEDULE}
    />
    <PrivateRoute
      path={ROUTE_PATHS.PERFORMANCE_COACHING}
      component={lazy(() => import('./App/PerformanceCoaching/PCRouter'))}
      permissions={PAGE_PERMISSIONS.PERFORMANCE_COACHING}
    />
    <PrivateRoute
      path={ROUTE_PATHS.BI_INSIGHTS}
      component={lazy(() => import('./App/InsightsBI'))}
      permissions={PAGE_PERMISSIONS.BI_INSIGHTS}
      featureFlag={FEATURE_FLAGS.BI_QS_EMBEDDING}
    />
    <PrivateRoute
      path={ROUTE_PATHS.LANDINGPAGE_MANAGEMENT}
      component={lazy(() => import('./App/ApplicationManagement/LandingPages'))}
      permissions={PAGE_PERMISSIONS.MANAGE_LANDING_PAGES}
      featureFlag={FEATURE_FLAGS.ENABLE_LANDINGPAGE_MANAGER}
    />
    <PrivateRoute
      path={ROUTE_PATHS.TRANSFER_USER_DATA}
      component={lazy(() => import('./App/ApplicationManagement/UserMerge'))}
      permissions={PAGE_PERMISSIONS.APPLICATION_MANAGEMENT}
    />
    <PrivateRoute
      path={ROUTE_PATHS.APPLICATION_MANAGEMENT}
      component={ApplicationManagement}
      permissions={PAGE_PERMISSIONS.APPLICATION_MANAGEMENT}
    />
    <PrivateRoute
      path={ROUTE_PATHS.BOT_ANALYTICS}
      component={BotAnalytics}
      permissions={PAGE_PERMISSIONS.BOT_ANALYTICS}
    />
    <PrivateRoute
      path={ROUTE_PATHS.TRENDING_TOPICS}
      component={TrendingTopics}
      permissions={PAGE_PERMISSIONS.TRENDING_TOPICS}
    />
    <PrivateRoute
      path={ROUTE_PATHS.AUTO_QA}
      component={AutoQA}
      permissions={PAGE_PERMISSIONS.AUTO_QA}
    />
    <PrivateRoute
      path={ROUTE_PATHS.AUTO_QA_FORM_MANAGER}
      component={AutoQAFormManager}
      permissions={PAGE_PERMISSIONS.AUTO_QA_RUN}
    />
    <PrivateRoute
      path={ROUTE_PATHS.SMART_TAGS}
      component={SmartTags}
      permissions={PAGE_PERMISSIONS.SMART_TAGS}
    />
    <PrivateRoute
      path={ROUTE_PATHS.SMART_TAG_APP}
      component={SmartTagsApp}
      permissions={PAGE_PERMISSIONS.SMART_TAGS_EDITOR_VIEW}
    />
    <Route
      path={ROUTE_PATHS.WELCOME_PAGE}
      component={WelcomePage}
    />
    <Route component={Status404NotFound} />
  </Switch>
)

